import { createApi } from '@reduxjs/toolkit/query/react';
import { PaginatedResponse, PaginationParams, SortParams, User, UserFilters } from '../../types';
import { baseQueryStub, shouldRefetchOnSortChange } from '../../utils';
import { UserService } from './user-service';

// A helper function to initialize the service once when needed
let userService: UserService | null = null;
const getUserService = () => {
  userService ??= new UserService();
  return userService;
};

// Define an API service using RTK Query that uses the UserService
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryStub,
  tagTypes: ['User'],
  serializeQueryArgs: ({ queryArgs, endpointName }) => {
    // For the getUsers endpoint, include sort in the cache key
    if (endpointName === 'getUsers') {
      const { pagination, filters, sort } = queryArgs as {
        pagination: PaginationParams;
        filters?: UserFilters;
        sort?: SortParams;
      };

      // Create a cache key that includes sort parameters
      return JSON.stringify({
        endpointName,
        pagination,
        filters,
        sort,
      });
    }

    // For other endpoints, use default serialization
    return JSON.stringify({ endpointName, queryArgs });
  },
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getUsers: builder.query<
      PaginatedResponse<User>,
      {
        pagination: PaginationParams;
        filters?: UserFilters;
        sort?: SortParams;
      }
    >({
      queryFn: async ({ pagination, filters, sort }) => {
        const { page, pageSize } = pagination;
        const userService = getUserService();
        const response = await userService.getUsers(page, pageSize, filters, sort);
        return { data: response };
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
      keepUnusedDataFor: 30, // 30 seconds
      forceRefetch: shouldRefetchOnSortChange,
    }),
    getAllUsers: builder.query<User[], void>({
      // For cases where all users are needed without pagination
      queryFn: async () => {
        // Get all users with a large page size
        const userService = getUserService();
        const response = await userService.getUsers(1, 1000);
        return { data: response.data };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
      keepUnusedDataFor: 60, // 60 seconds
    }),
    getUserById: builder.query<User, string>({
      queryFn: async id => {
        const userService = getUserService();
        const user = await userService.getUserById(id);
        return { data: user };
      },
      providesTags: (_, __, id) => [{ type: 'User', id }],
    }),
    addUser: builder.mutation<User, { userData: Omit<User, 'id'>; avatarFile?: File }>({
      queryFn: async ({ userData, avatarFile }) => {
        const userService = getUserService();
        const newUser = await userService.createUser(userData, avatarFile);
        return { data: newUser };
      },
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation<void, Partial<User> & { id: string; avatarFile?: File }>({
      queryFn: async User => {
        const { id, avatarFile, ...data } = User;
        const userService = getUserService();
        await userService.updateUser(id, data, avatarFile);
        return { data: undefined };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),
    deleteUser: builder.mutation<void, string>({
      queryFn: async id => {
        const userService = getUserService();
        await userService.deleteUser(id);
        return { data: undefined };
      },
      invalidatesTags: (_, __, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  usePrefetch,
} = usersApi;
